<template>
  <base-dialog
    :show="dialogVisible"
    :title="title"
    fixed-width="460"
    :actionName="'Apply Discount'"
    :actionDisabled="isApplyDisabled"
    :showCancel="'Cancel'"
    :subActionName="scope === 'item' && hasExistingDiscount ? 'Remove' : ''"
    subActionColor="error"
    @close="closeDialog"
    @action="handleApply"
    @subAction="removeDiscount"
  >
    <template #content>
      <div v-if="scope === 'item' && targetItem" class="discount-target mb-3">
        <div class="d-flex align-center ga-3">
          <v-avatar color="primary" variant="tonal" size="36">
            <v-icon icon="mdi-package-variant-closed" size="20" />
          </v-avatar>
          <div class="min-width-0">
            <div class="text-body-2 font-weight-medium text-truncate">
              {{ getItemTitle(targetItem) }}
            </div>
            <div class="text-caption text-medium-emphasis text-truncate">
              {{ getItemSubtitle(targetItem) }}
            </div>
          </div>
        </div>
      </div>

      <v-alert
        v-if="scope === 'cart'"
        type="info"
        variant="tonal"
        density="comfortable"
        class="mb-3"
      >
        Applying a cart-wide discount overrides all existing item discounts.
        <span v-if="cartStore.hasAnyDiscounts"> Any current discounts will be replaced.</span>
      </v-alert>

      <div class="app-field-label mb-1">Discount type</div>
      <v-radio-group v-model="selectedType" inline density="compact" class="mb-2 radio-type-group">
        <v-radio label="Percentage" value="percentage" />
        <v-radio label="Fixed amount" value="fixed" />
      </v-radio-group>

      <v-text-field
        v-if="isPercentage"
        v-model="percentageInput"
        type="number"
        placeholder="0"
        density="comfortable"
        variant="outlined"
        step="0.1"
        min="0"
        max="100"
        :error-messages="validationMessage"
        hide-spin-buttons
      />

      <v-text-field
        v-else
        v-model="fixedValueInput"
        type="number"
        placeholder="0.00"
        density="comfortable"
        variant="outlined"
        :prefix="currencySymbol"
        min="0"
        step="0.01"
        :hint="fixedAmountHint"
        persistent-hint
        :error-messages="validationMessage"
        hide-spin-buttons
      />

      <div v-if="scope === 'item' && itemPreview" class="mt-4">
        <div class="modal-summary">
          <div class="modal-summary-row">
            <span class="app-field-label">Original unit price</span>
            <span
              class="text-body-2 text-error text-decoration-line-through"
              v-html="formatMoney({ amount: itemPreview.baseUnit, currency: currency })"
            />
          </div>
          <div class="modal-summary-row">
            <span class="app-field-label">New unit price</span>
            <span
              class="text-body-2 text-success"
              v-html="formatMoney({ amount: itemPreview.netUnit, currency: currency })"
            />
          </div>
          <v-divider class="my-2" />
          <div class="modal-summary-row">
            <span class="app-field-label">Original line total</span>
            <span
              class="text-body-2 text-error text-decoration-line-through"
              v-html="formatMoney({ amount: itemPreview.baseSubtotal, currency: currency })"
            />
          </div>
          <div class="modal-summary-row total">
            <span class="text-subtitle-1 font-weight-medium">New line total</span>
            <span
              class="text-subtitle-1"
              v-html="formatMoney({ amount: itemPreview.netSubtotal, currency: currency })"
            />
          </div>
        </div>
      </div>

      <div v-else-if="scope === 'cart' && cartPreview" class="mt-4">
        <div class="modal-summary">
          <div class="modal-summary-row">
            <span class="app-field-label">Original subtotal</span>
            <span
              class="text-body-2 text-error text-decoration-line-through"
              v-html="formatMoney({ amount: cartPreview.subtotal, currency: currency })"
            />
          </div>
          <div class="modal-summary-row">
            <span class="app-field-label">Discounts applied</span>
            <span
              class="text-body-2 text-success"
              v-html="formatMoney({ amount: cartPreview.discountTotal * -1, currency: currency })"
            />
          </div>
          <v-divider class="my-2" />
          <div class="modal-summary-row total">
            <span class="text-subtitle-1 font-weight-medium">Subtotal</span>
            <span
              class="text-subtitle-1"
              v-html="formatMoney({ amount: cartPreview.netSubtotal, currency: currency })"
            />
          </div>
        </div>
        <div v-if="cartPreview.undistributed > 0" class="text-caption text-warning mt-1">
          Unable to distribute
          {{ formatPlainCurrency(cartPreview.undistributed) }}
          due to line quantities. Actual discount will be slightly lower.
        </div>
      </div>
    </template>
  </base-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useCartStore } from "@/cart/stores/cart";
import type { CartItem, DiscountType, DiscountScope } from "@/cart/types/cart";
import { formatMoney } from "@/shared/services/util.service";
import {
  allocateFixedCartDiscounts,
  computeDiscountAmount,
  hasDiscountApplied,
  sanitizeFixedAmount,
  sanitizePercentage,
} from "@/shared/utils/discount.utils";
import { getCartItemProductClassName, getCartItemProductName } from "@/cart/utils/cart.utils";
import BaseDialog from "@/shared/components/Common/BaseDialog.vue";

const props = defineProps<{
  modelValue: boolean;
  scope: DiscountScope;
  itemId?: string | null;
}>();

const emit = defineEmits(["update:modelValue"]);

const cartStore = useCartStore();
const cartItems = computed(() => cartStore.items);
const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const targetItem = computed<CartItem | null>(() => {
  if (props.scope !== "item" || !props.itemId) {
    return null;
  }
  return cartItems.value.find((item) => item.id === props.itemId) ?? null;
});

const currency = computed(() => {
  if (props.scope === "item" && targetItem.value) {
    return targetItem.value.currency;
  }
  return cartItems.value[0]?.currency || "usd";
});

const currencySymbol = computed(() => {
  const parts = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: (currency.value || "usd").toUpperCase(),
  }).formatToParts(0);
  return parts.find((part) => part.type === "currency")?.value || "$";
});

const title = computed(() => (props.scope === "item" ? "Item discount" : "Cart discount"));

const selectedType = ref<DiscountType>("percentage");
const percentageInput = ref<string>("");
const fixedValueInput = ref<string>("");

const isPercentage = computed(() => selectedType.value === "percentage");
const hasExistingDiscount = computed(() =>
  props.scope === "item" && targetItem.value ? hasDiscountApplied(targetItem.value) : false,
);

const percentageValue = computed(() => {
  const parsed = parseFloat(percentageInput.value);
  return Number.isFinite(parsed) ? parsed : 0;
});

const fixedValueCents = computed(() => {
  const parsed = parseFloat(fixedValueInput.value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.max(0, Math.floor(parsed * 100));
});

const maxFixedValue = computed(() => {
  if (props.scope === "item" && targetItem.value) {
    return targetItem.value.unitAmount;
  }
  return cartStore.preDiscountSubtotal;
});

const sanitizedPercent = computed(() => sanitizePercentage(percentageValue.value));
const sanitizedFixed = computed(() =>
  sanitizeFixedAmount(fixedValueCents.value, maxFixedValue.value),
);

const itemPreview = computed(() => {
  if (props.scope !== "item" || !targetItem.value) {
    return null;
  }

  const baseUnit = targetItem.value.unitAmount;
  const discountAmount =
    selectedType.value === "percentage"
      ? computeDiscountAmount(baseUnit, "percentage", sanitizedPercent.value)
      : computeDiscountAmount(baseUnit, "fixed", sanitizedFixed.value);

  const netUnit = Math.max(0, baseUnit - discountAmount);
  const baseSubtotal = baseUnit * targetItem.value.quantity;
  const netSubtotal = netUnit * targetItem.value.quantity;

  return {
    baseUnit,
    netUnit,
    baseSubtotal,
    netSubtotal,
  };
});

const cartPreview = computed(() => {
  if (props.scope !== "cart") {
    return null;
  }
  const subtotal = cartStore.preDiscountSubtotal;
  if (subtotal <= 0) {
    return null;
  }

  if (selectedType.value === "percentage") {
    const ratio = sanitizePercentage(percentageValue.value) / 100;
    if (ratio <= 0) {
      return {
        subtotal,
        netSubtotal: subtotal,
        discountTotal: 0,
        undistributed: 0,
      };
    }
    const netSubtotal = cartItems.value.reduce((sum, item) => {
      const unitDiscount = computeDiscountAmount(
        item.unitAmount,
        "percentage",
        sanitizedPercent.value,
      );
      const netUnit = Math.max(0, item.unitAmount - unitDiscount);
      return sum + netUnit * item.quantity;
    }, 0);
    return {
      subtotal,
      netSubtotal,
      discountTotal: Math.max(0, subtotal - netSubtotal),
      undistributed: 0,
    };
  }

  const allocation = allocateFixedCartDiscounts(cartItems.value, sanitizedFixed.value);

  if (!allocation.perUnitDiscounts.length) {
    return {
      subtotal,
      netSubtotal: subtotal,
      discountTotal: 0,
      undistributed: 0,
    };
  }

  const netSubtotal = cartItems.value.reduce((sum, item, index) => {
    const unitDiscount = Math.min(item.unitAmount, allocation.perUnitDiscounts[index] ?? 0);
    const netUnit = Math.max(0, item.unitAmount - unitDiscount);
    return sum + netUnit * item.quantity;
  }, 0);

  return {
    subtotal,
    netSubtotal,
    discountTotal: Math.max(0, subtotal - netSubtotal),
    undistributed: allocation.undistributed,
  };
});

const validationMessage = computed(() => {
  if (isPercentage.value && percentageValue.value > 100) {
    return "Percentage cannot exceed 100%.";
  }
  if (!isPercentage.value && fixedValueCents.value > maxFixedValue.value) {
    return "Amount cannot exceed the original total.";
  }
  return "";
});

const isApplyDisabled = computed(() => {
  if (props.scope === "item" && !targetItem.value) {
    return true;
  }

  if (isPercentage.value) {
    return sanitizedPercent.value <= 0 || !!validationMessage.value;
  }

  return sanitizedFixed.value <= 0 || !!validationMessage.value;
});

const formatPlainCurrency = (amount: number) => {
  const formatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: (currency.value || "usd").toUpperCase(),
  });
  return formatter.format((amount ?? 0) / 100);
};

const fixedAmountHint = computed(() => {
  if (maxFixedValue.value <= 0) {
    return "No discount available.";
  }
  return `Up to ${formatPlainCurrency(maxFixedValue.value)}`;
});

const closeDialog = () => {
  emit("update:modelValue", false);
};

const resetForm = () => {
  selectedType.value = "percentage";
  percentageInput.value = "";
  fixedValueInput.value = "";

  if (props.scope === "item" && targetItem.value?.discountType) {
    selectedType.value = targetItem.value.discountType;
    if (targetItem.value.discountType === "percentage") {
      percentageInput.value = String(targetItem.value.discountTypeValue ?? "");
    } else {
      fixedValueInput.value = centsToInput(targetItem.value.discountTypeValue ?? 0);
    }
  } else if (props.scope === "cart" && cartStore.cartDiscount) {
    selectedType.value = cartStore.cartDiscount.type;
    if (cartStore.cartDiscount.type === "percentage") {
      percentageInput.value = String(cartStore.cartDiscount.value ?? "");
    } else {
      fixedValueInput.value = centsToInput(cartStore.cartDiscount.value ?? 0);
    }
  }
};

const clampDecimalPlaces = (value: string, places = 2): string => {
  if (!value || !value.includes(".")) {
    return value;
  }
  const [intPart, decimalPart = ""] = value.split(".");
  if (decimalPart.length <= places) {
    return value;
  }
  return `${intPart}.${decimalPart.slice(0, places)}`;
};

watch(
  () => props.modelValue,
  (visible) => {
    if (visible) {
      resetForm();
    }
  },
);

watch(
  () => props.itemId,
  () => {
    if (props.modelValue) {
      resetForm();
    }
  },
);

watch(
  () => props.scope,
  () => {
    if (props.modelValue) {
      resetForm();
    }
  },
);

watch(percentageInput, (value) => {
  if (!value) {
    return;
  }
  const clamped = clampDecimalPlaces(value);
  if (clamped !== value) {
    percentageInput.value = clamped;
  }
});

watch(fixedValueInput, (value) => {
  if (!value) {
    return;
  }
  const clamped = clampDecimalPlaces(value);
  if (clamped !== value) {
    fixedValueInput.value = clamped;
  }
});

const handleApply = () => {
  if (isApplyDisabled.value) {
    return;
  }

  if (props.scope === "item" && targetItem.value) {
    const value =
      selectedType.value === "percentage" ? sanitizedPercent.value : sanitizedFixed.value;
    cartStore.applyItemDiscount(targetItem.value.id, {
      type: selectedType.value,
      value,
    });
    closeDialog();
    return;
  }

  if (props.scope === "cart") {
    const value =
      selectedType.value === "percentage" ? sanitizedPercent.value : sanitizedFixed.value;
    const success = cartStore.applyCartDiscount({
      type: selectedType.value,
      value,
    });
    if (success) {
      closeDialog();
    }
  }
};

const removeDiscount = () => {
  if (props.scope === "item" && targetItem.value) {
    cartStore.clearItemDiscount(targetItem.value.id);
    closeDialog();
  }
};

const getItemTitle = (item: CartItem) => {
  const productName = getCartItemProductName(item);
  if (productName) {
    return productName;
  }

  const productClassName = getCartItemProductClassName(item);
  if (productClassName) {
    return productClassName;
  }

  return "Cart item";
};

const getItemSubtitle = (item: CartItem) => {
  return item.priceName || "Standard";
};

const centsToInput = (value: number | undefined) => {
  if (!value) {
    return "";
  }
  return (value / 100).toFixed(2);
};
</script>

<style scoped>
.discount-target {
  border-radius: 12px;
  padding: 12px;
  background: rgba(var(--v-theme-primary), 0.06);
}

.min-width-0 {
  min-width: 0;
}

.modal-summary {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.02);
  margin-top: 16px;
}

.modal-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.modal-summary-row + .modal-summary-row {
  margin-top: 8px;
}

.modal-summary-row span:last-child {
  white-space: nowrap;
}

.modal-summary-row.total span:last-child {
  font-weight: 600;
}

.radio-type-group :deep(.v-radio) {
  margin-right: 16px;
}
</style>
